<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" :schema="schema" :state="state" :validate-on="['submit']" class="h-full" @submit="save">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            {{ post ? 'Edit Post' : 'Add Post' }}
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="name" label="Name" required>
                        <UInput v-model="state.name" type="text" size="xl" placeholder="Faraya" />
                    </UFormGroup>
                    <UFormGroup name="code" label="Code" help="Lowercase letters, digits, - or _. Used to tag patients." required>
                        <UInput v-model="state.code" type="text" size="xl" placeholder="faraya" :disabled="!!post || !!createdPostId" />
                    </UFormGroup>
                    <UFormGroup name="areasText" label="Areas" help="One per line, e.g. Beginner, Off-piste, Snowpark.">
                        <UTextarea v-model="state.areasText" :rows="5" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="is_active" label="Status">
                        <div class="relative border border-gray-200 rounded-md p-4 mt-2">
                            <div class="flex items-center justify-between">
                                <p class="text-sm font-medium">Active</p>
                                <UToggle v-model="state.is_active" size="xs" color="primary" />
                            </div>
                        </div>
                    </UFormGroup>
                    <UFormGroup name="storages" label="Storage Spots">
                        <div class="space-y-2 mt-2">
                            <div v-for="(storage, index) in state.storages" :key="storage.id ?? `new-${index}`" class="flex items-center space-x-2">
                                <UInput v-model="storage.name" class="flex-1" placeholder="Emergency Cart" :disabled="storage.is_archived" />
                                <UButton v-if="storage.id" color="gray" variant="ghost"
                                    :icon="storage.is_archived ? 'i-ph-arrow-counter-clockwise-bold' : 'i-ph-archive-bold'"
                                    @click="storage.is_archived = !storage.is_archived" />
                                <UButton v-else color="gray" variant="ghost" icon="i-ph-trash-bold" @click="state.storages.splice(index, 1)" />
                            </div>
                            <UButton color="white" size="sm" icon="i-ph-plus-bold" label="Add Spot"
                                @click="state.storages.push({ name: '', is_archived: false })" />
                        </div>
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton size="lg" color="white" @click="dismiss()">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" color="black" :loading="saving">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FieldPost } from '~/types/fieldPost'

interface StorageDraft {
    id?: string
    name: string
    is_archived: boolean
}

const props = defineProps<{
    open: boolean
    post?: FieldPost
}>()

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const client = useSupabaseClient()
const toast = useToast()

const form = ref()
const saving = ref(false)
const createdPostId = ref<string | null>(null)
const state = reactive({
    name: '',
    code: '',
    areasText: '',
    is_active: true,
    storages: [] as StorageDraft[],
})

const schema = z.object({
    name: z.string().trim().min(1, 'Name is required.'),
    code: z.string().trim().toLowerCase().regex(/^[a-z0-9_-]+$/, 'Use lowercase letters, digits, - or _.'),
})

watch(() => props.open, (open) => {
    if (!open) return
    createdPostId.value = null
    state.name = props.post?.name ?? ''
    state.code = props.post?.code ?? ''
    state.areasText = (props.post?.areas ?? []).join('\n')
    state.is_active = props.post?.is_active ?? true
    state.storages = [...(props.post?.storages ?? [])]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(s => ({ id: s.id, name: s.name, is_archived: s.is_archived }))
})

async function save() {
    const payload = {
        name: state.name.trim(),
        code: state.code.trim().toLowerCase(),
        areas: state.areasText.split('\n').map(a => a.trim()).filter(Boolean),
        is_active: state.is_active,
    }

    saving.value = true
    try {
        const existingId = props.post?.id ?? createdPostId.value
        const { data: saved, error } = existingId
            ? await client.from('field_posts').update(payload as never).eq('id', existingId).select('id').single()
            : await client.from('field_posts').insert(payload as never).select('id').single()
        if (error || !saved) throw error

        const postId = (saved as { id: string }).id
        if (!createdPostId.value) createdPostId.value = postId

        const rows = state.storages
            .filter(s => s.name.trim())
            .map((s, index) => ({ id: s.id, post_id: postId, name: s.name.trim(), sort_order: index, is_archived: s.is_archived }))

        const existing = rows.filter(r => r.id)
        let created = rows.filter(r => !r.id).map(({ id, ...rest }) => rest)

        if (created.length) {
            const { data: existingStorages } = await client
                .from('field_post_storages')
                .select('id, name')
                .eq('post_id', postId)
            if (existingStorages) {
                const storagesByName = new Map(existingStorages.map(s => [s.name, s.id]))
                created = created.map(s => {
                    const existingId = storagesByName.get(s.name)
                    return existingId ? { ...s, id: existingId } : s
                })
                const withIds = created.filter(r => r.id)
                created = created.filter(r => !r.id)
                if (withIds.length) {
                    existing.push(...(withIds as any))
                }
            }
        }

        if (existing.length) {
            const { error: e } = await client.from('field_post_storages').upsert(existing as never)
            if (e) throw e
        }
        if (created.length) {
            const { error: e } = await client.from('field_post_storages').insert(created as never)
            if (e) throw e
        }

        toast.add({ title: 'Post saved.', color: 'green', icon: 'i-ph-check-circle-duotone' })
        emit('reload')
        dismiss()
    } catch (e: any) {
        if (createdPostId.value) {
            emit('reload')
            toast.add({ title: 'Could not save storage spots. Fix and try again.', description: e?.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
        } else {
            toast.add({ title: 'Could not save the post.', description: e?.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
        }
    } finally {
        saving.value = false
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>
